import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment/config";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { minutesToMilliseconds } from "date-fns";
import { z } from "zod";

const workplaceGuideStepSchema = z.object({
  icon: z.string(),
  title: z.string(),
  description: z.string(),
  pictureUrl: z.string(),
});
const workplaceGuideResponseSchema = z.object({
  data: z.object({
    attributes: z.object({
      steps: z.array(workplaceGuideStepSchema),
    }),
  }),
});

export type WorkplaceGuideStep = z.infer<typeof workplaceGuideStepSchema>;
export type WorkplaceGuideResponse = z.infer<typeof workplaceGuideResponseSchema>;

function getWorkplaceGuideUrl(workplaceId: string, category: string): string {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/guides/${category}`;
}

interface UseWorkplaceGuideParams {
  workplaceId: string;
  category: string;
}

export function useWorkplaceGuide(
  queryParams: UseWorkplaceGuideParams,
  options: UseGetQueryOptions<WorkplaceGuideResponse> = {}
) {
  const isTimeclockDirectionsEnabled = useCbhFlag(CbhFeatureFlag.HACKATHON_TIMECLOCK_DIRECTIONS, {
    defaultValue: false,
  });

  const { workplaceId, category } = queryParams;

  return useGetQuery({
    url: getWorkplaceGuideUrl(workplaceId, category),
    queryParams: {},
    requestSchema: z.object({}),
    responseSchema: workplaceGuideResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_FACILITY_PROFILE_FAILURE,
    },
    // 404 errors are allowed here. If we don't set this to false, they're catched
    // by the global error boundary.
    // FIXME: https://linear.app/clipboardhealth/issue/FEF-579/add-allowederrorstatuscodes-to-the-api
    useErrorBoundary: false,
    staleTime: minutesToMilliseconds(10),
    enabled: isDefined(workplaceId) && isTimeclockDirectionsEnabled,
    ...options,
  });
}
